import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Select,
  Button,
  Upload,
  Steps,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  Progress,
  Alert,
  message
} from 'antd';
import {
  InboxOutlined,
  PlayCircleOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useStore } from '../../stores/useStore';
import { apiService } from '../../services/api';
import { ANALYSIS_TEMPLATES } from '../../utils/constants';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;
const { Step } = Steps;

interface ProjectSpec {
  project_name: string;
  research_questions: string[];
  media_types: string[];
  content_categories: string[];
  analysis_templates: string[];
  created_by: string;
  study_template_id?: string;
}

const StudyBuilder: React.FC = () => {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const [analysisRunning, setAnalysisRunning] = useState(false);

  const {
    studyTemplates,
    selectedTemplate,
    currentMediaTypes,
    currentContentCategories,
    setStudyTemplates,
    setSelectedTemplate,
    setCurrentMediaTypes,
    setCurrentContentCategories
  } = useStore();

  useEffect(() => {
    loadStudyTemplates();
    if (studyId) {
      loadProject(studyId);
    }
  }, [studyId]);

  const loadStudyTemplates = async () => {
    try {
      // Mock study templates - replace with actual API call
      const mockTemplates = [
        {
          template_id: '1',
          template_name: 'Media & Entertainment Analysis',
          description: 'For analyzing movies, TV shows, and digital content',
          media_types: [
            { label: 'Scripts', value: 'script', file_extensions: ['.txt', '.pdf', '.docx'] },
            { label: 'Transcripts', value: 'transcript', file_extensions: ['.txt', '.srt', '.vtt'] },
            { label: 'Audio Files', value: 'audio', file_extensions: ['.mp3', '.wav', '.m4a'] },
            { label: 'Video Files', value: 'video', file_extensions: ['.mp4', '.mov', '.avi'] }
          ],
          content_categories: [
            { label: 'Movies', value: 'movie', description: 'Feature films and documentaries' },
            { label: 'TV Shows', value: 'tv_show', description: 'Television series and episodes' },
            { label: 'Podcasts', value: 'podcast', description: 'Audio podcasts and shows' },
            { label: 'YouTube', value: 'youtube', description: 'YouTube videos and content' }
          ],
          default_analysis_templates: ['archetypes', 'themes', 'representation'],
          created_by: 'system',
          is_public: true
        },
        {
          template_id: '2',
          template_name: 'Print Media Analysis',
          description: 'For analyzing books, magazines, and newspapers',
          media_types: [
            { label: 'Books', value: 'book', file_extensions: ['.epub', '.mobi', '.pdf', '.txt'] },
            { label: 'Magazines', value: 'magazine', file_extensions: ['.pdf', '.txt', '.docx'] },
            { label: 'Newspapers', value: 'newspaper', file_extensions: ['.pdf', '.txt', '.html'] },
            { label: 'Articles', value: 'article', file_extensions: ['.txt', '.docx', '.html'] }
          ],
          content_categories: [
            { label: 'Sports', value: 'sports', description: 'Sports coverage and analysis' },
            { label: 'Video Games', value: 'gaming', description: 'Gaming industry and culture' },
            { label: 'Beauty', value: 'beauty', description: 'Beauty, fashion, and lifestyle' },
            { label: 'Technology', value: 'tech', description: 'Technology and innovation' },
            { label: 'Politics', value: 'politics', description: 'Political news and commentary' }
          ],
          default_analysis_templates: ['themes', 'sentiment', 'representation'],
          created_by: 'system',
          is_public: true
        },
        {
          template_id: '3',
          template_name: 'Custom Study',
          description: 'Define your own media types and categories',
          media_types: [],
          content_categories: [],
          default_analysis_templates: ['themes'],
          created_by: 'system',
          is_public: true
        }
      ];

      setStudyTemplates(mockTemplates);
    } catch (error) {
      message.error('Failed to load study templates');
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = studyTemplates.find(t => t.template_id === templateId);
    if (template) {
      setSelectedTemplate(template);
      setCurrentMediaTypes(template.media_types);
      setCurrentContentCategories(template.content_categories);
      
      // Pre-populate form with template defaults
      form.setFieldsValue({
        study_template_id: templateId,
        media_types: template.media_types.map(mt => mt.value),
        content_categories: template.content_categories.map(cc => cc.value),
        analysis_templates: template.default_analysis_templates
      });
    }
  };

  const loadProject = async (id: string) => {
    try {
      // Mock API call - replace with actual API
      const mockProject = {
        project_name: 'AIBM Fatherhood Study',
        research_questions: [
          'How are fathers portrayed in modern media?',
          'What representation gaps exist in fatherhood narratives?'
        ],
        media_types: ['script', 'transcript'],
        content_categories: ['movie', 'tv_show'],
        analysis_templates: ['archetypes', 'themes', 'representation'],
        created_by: 'researcher@nrg.com'
      };
      
      form.setFieldsValue(mockProject);
      setCurrentStep(1); // Assume project setup is complete
    } catch (error) {
      message.error('Failed to load project');
    }
  };

  const handleProjectSave = async (values: ProjectSpec) => {
    setLoading(true);
    try {
      // API call to save project
      await new Promise(resolve => setTimeout(resolve, 1000)); // Mock delay
      message.success('Project saved successfully');
      setCurrentStep(1);
    } catch (error) {
      message.error('Failed to save project');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = {
    name: 'files',
    multiple: true,
    accept: currentMediaTypes.flatMap(mt => mt.file_extensions).join(','),
    customRequest: async ({ file, onSuccess, onError }: any) => {
      try {
        // Mock file upload
        await new Promise(resolve => setTimeout(resolve, 1000));
        setUploadedFiles(prev => [...prev, {
          uid: Date.now(),
          name: file.name,
          status: 'done',
          size: file.size,
          type: file.type
        }]);
        onSuccess?.();
        message.success(`${file.name} uploaded successfully`);
      } catch (error) {
        onError?.(error);
        message.error(`Failed to upload ${file.name}`);
      }
    },
  };

  const startAnalysis = async () => {
    if (uploadedFiles.length === 0) {
      message.warning('Please upload media files first');
      return;
    }

    setAnalysisRunning(true);
    setAnalysisProgress(0);

    // Simulate analysis progress
    const interval = setInterval(() => {
      setAnalysisProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setAnalysisRunning(false);
          message.success('Analysis completed successfully!');
          setCurrentStep(2);
          return 100;
        }
        return prev + 10;
      });
    }, 500);
  };

  const steps = [
    {
      title: 'Project Setup',
      description: 'Define research scope and questions'
    },
    {
      title: 'Media Upload',
      description: 'Upload and organize content files'
    },
    {
      title: 'Analysis',
      description: 'Run AI analysis pipeline'
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Space>
        <Title level={2} style={{ marginTop: 16 }}>
          {studyId ? 'Edit Study' : 'New Study'}
        </Title>
        <Paragraph>
          Set up your research project, upload media files, and configure AI analysis templates.
        </Paragraph>
      </div>

      <Steps current={currentStep} style={{ marginBottom: 32 }}>
        {steps.map((step, index) => (
          <Step key={index} title={step.title} description={step.description} />
        ))}
      </Steps>

      {/* Step 0: Project Setup */}
      {currentStep === 0 && (
        <Card title="Project Configuration">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleProjectSave}
          >
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Form.Item
                  name="study_template_id"
                  label="Study Template"
                  rules={[{ required: true, message: 'Please select a study template' }]}
                >
                  <Select 
                    placeholder="Select a study template"
                    onChange={handleTemplateSelect}
                  >
                    {studyTemplates.map(template => (
                      <Select.Option key={template.template_id} value={template.template_id}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{template.template_name}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>{template.description}</div>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="project_name"
                  label="Project Name"
                  rules={[{ required: true, message: 'Please enter project name' }]}
                >
                  <Input placeholder="e.g., AIBM Fatherhood Study" />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item
                  name="research_questions"
                  label="Research Questions"
                  rules={[{ required: true, message: 'Please enter research questions' }]}
                >
                  <TextArea
                    rows={4}
                    placeholder="Enter each research question on a new line"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  name="media_types"
                  label="Media Types"
                  rules={[{ required: true, message: 'Please select media types' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select media types"
                    disabled={!selectedTemplate}
                  >
                    {currentMediaTypes.map(type => (
                      <Select.Option key={type.value} value={type.value}>
                        <div>
                          <div>{type.label}</div>
                          <div style={{ fontSize: '11px', color: '#666' }}>
                            {type.file_extensions.join(', ')}
                          </div>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  name="content_categories"
                  label="Content Categories"
                  rules={[{ required: true, message: 'Please select content categories' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select categories"
                    disabled={!selectedTemplate}
                  >
                    {currentContentCategories.map(category => (
                      <Select.Option key={category.value} value={category.value}>
                        <div>
                          <div>{category.label}</div>
                          {category.description && (
                            <div style={{ fontSize: '11px', color: '#666' }}>
                              {category.description}
                            </div>
                          )}
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  name="analysis_templates"
                  label="Analysis Templates"
                  rules={[{ required: true, message: 'Please select analysis templates' }]}
                >
                  <Select
                    mode="multiple"
                    placeholder="Select analysis types"
                  >
                    {ANALYSIS_TEMPLATES.map(template => (
                      <Select.Option key={template.value} value={template.value}>
                        {template.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {selectedTemplate && (
              <Alert
                message={`Using ${selectedTemplate.template_name} Template`}
                description={selectedTemplate.description}
                type="info"
                style={{ marginBottom: 16 }}
              />
            )}

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                icon={<SaveOutlined />}
              >
                Save Project Configuration
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      {/* Step 1: Media Upload */}
      {currentStep === 1 && (
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Upload Media Files">
              {selectedTemplate && (
                <Alert
                  message="Supported File Types"
                  description={
                    <div>
                      {currentMediaTypes.map(type => (
                        <Tag key={type.value} style={{ margin: 2 }}>
                          {type.label}: {type.file_extensions.join(', ')}
                        </Tag>
                      ))}
                    </div>
                  }
                  type="info"
                  style={{ marginBottom: 16 }}
                />
              )}

              <Dragger {...handleFileUpload} style={{ marginBottom: 16 }}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag files to this area to upload
                </p>
                <p className="ant-upload-hint">
                  Upload your study materials based on the selected template
                </p>
              </Dragger>

              {uploadedFiles.length > 0 && (
                <div>
                  <Title level={4}>Uploaded Files ({uploadedFiles.length})</Title>
                  <div style={{ marginBottom: 16 }}>
                    {uploadedFiles.map((file, index) => (
                      <Tag key={index} style={{ margin: 4 }}>
                        {file.name}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              <Button 
                type="primary" 
                icon={<PlayCircleOutlined />}
                onClick={startAnalysis}
                disabled={uploadedFiles.length === 0}
              >
                Start Analysis
              </Button>
            </Card>
          </Col>
        </Row>
      )}

      {/* Step 2: Analysis Progress */}
      {currentStep === 2 || analysisRunning ? (
        <Card title="Analysis Pipeline">
          <Alert
            message="Analysis in Progress"
            description="AI models are processing your content. This may take several minutes."
            type="info"
            style={{ marginBottom: 16 }}
          />

          <Progress 
            percent={analysisProgress} 
            status={analysisRunning ? "active" : "success"}
            style={{ marginBottom: 16 }}
          />

          {analysisProgress === 100 && (
            <div>
              <Alert
                message="Analysis Complete!"
                description="Your content has been analyzed successfully. You can now explore the results in the workbench."
                type="success"
                icon={<CheckCircleOutlined />}
                style={{ marginBottom: 16 }}
              />

              <Space>
                <Button 
                  type="primary"
                  onClick={() => navigate(`/metadata-explorer/${studyId || 'new'}`)}
                >
                  Explore Results
                </Button>
                <Button onClick={() => navigate(`/insight-console/${studyId || 'new'}`)}>
                  Start Analysis Session
                </Button>
              </Space>
            </div>
          )}
        </Card>
      ) : null}
    </div>
  );
};

export default StudyBuilder; 